import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import type { Destination } from '@ia/shared';
import { api, errorMessage } from '../api';
import { useIsAdmin } from '../auth';
import { Alert, ConfirmDialog, Dialog, Empty, Field, Spinner, When } from '../components/ui';
import { formatDate, relative } from '../util';

export function DestinationsPage() {
  const isAdmin = useIsAdmin();
  const [rows, setRows] = useState<Destination[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState<Destination | null>(null);
  const [editPat, setEditPat] = useState<Destination | null>(null);
  const [clearPat, setClearPat] = useState<Destination | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await api.destinations());
      setError(null);
    } catch (e) {
      setError(errorMessage(e));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const replace = (d: Destination) => setRows((rs) => rs?.map((r) => (r.id === d.id ? d : r)) ?? null);

  return (
    <div className="page">
      <div className="page-head">
        <h1>Destinations</h1>
      </div>
      <Alert kind="info">
        A destination is a Maximizer tenant that advisors have sent batches to. The <strong>first write</strong> of a
        batch uses the advisor's live Maximizer session from the extension. <strong>Replays</strong> run without the
        advisor, so they use the tenant's personal access token (PAT) configured here. Without a PAT, replays are
        disabled for that tenant.
      </Alert>
      {error && <Alert>{error}</Alert>}
      {notice && <Alert kind="ok">{notice}</Alert>}
      {!rows && !error && <Spinner />}
      {rows && rows.length === 0 && (
        <Empty>No destinations yet. They appear automatically the first time an advisor submits a batch.</Empty>
      )}
      {rows && rows.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Label</th>
                <th>Workspace / Octopus URL</th>
                <th>Maximizer users seen</th>
                <th className="num">Batches</th>
                <th>Replay PAT</th>
                {isAdmin && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id}>
                  <td>
                    <div>{d.displayName ?? <span className="muted">(no display name)</span>}</div>
                    <code className="small">{d.database}</code>
                    <div className="muted small">first seen {formatDate(d.createdAt)}</div>
                  </td>
                  <td>{d.label ?? <span className="muted">—</span>}</td>
                  <td>
                    <div className="small">{d.workspaceId ?? <span className="pat-missing">no workspace id — Process disabled</span>}</div>
                    <div className="small">
                      tenant {d.tenantId ?? <span className="pat-missing">not set — Process disabled</span>}
                    </div>
                    <code className="small break">{d.octopusBaseUrl}</code>
                  </td>
                  <td>
                    {d.users.length === 0 ? (
                      <span className="muted">—</span>
                    ) : (
                      <ul className="plain-list">
                        {d.users.map((u) => (
                          <li key={u.uid}>
                            {u.displayName ?? u.uid}{' '}
                            <span className="muted small" title={formatDate(u.lastSeenAt)}>
                              {relative(u.lastSeenAt)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td className="num">
                    <Link to={`/batches?bucket=all&destinationId=${encodeURIComponent(d.id)}`}>{d.batchCount}</Link>
                  </td>
                  <td>
                    {d.hasPat ? (
                      <span className="pat-ok">
                        PAT configured
                        {d.patUpdatedAt && (
                          <>
                            {' '}
                            on <When iso={d.patUpdatedAt} inline />
                          </>
                        )}
                      </span>
                    ) : (
                      <span className="pat-missing">No PAT — replays disabled</span>
                    )}
                  </td>
                  {isAdmin && (
                    <td>
                      <div className="btn-row">
                        <button className="btn btn-sm" onClick={() => setEditLabel(d)}>
                          Edit
                        </button>
                        <button className="btn btn-sm" onClick={() => setEditPat(d)}>
                          {d.hasPat ? 'Replace PAT' : 'Set PAT'}
                        </button>
                        {d.hasPat && (
                          <button className="btn btn-sm btn-danger-ghost" onClick={() => setClearPat(d)}>
                            Clear PAT
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editLabel && (
        <LabelDialog
          dest={editLabel}
          onClose={() => setEditLabel(null)}
          onSaved={(d) => {
            replace(d);
            setEditLabel(null);
            setNotice(`Destination ${d.database} updated.`);
          }}
        />
      )}
      {editPat && (
        <PatDialog
          dest={editPat}
          onClose={() => setEditPat(null)}
          onSaved={(d) => {
            replace(d);
            setEditPat(null);
            setNotice(`PAT saved for ${d.database}. Replays are now enabled for this tenant.`);
          }}
        />
      )}
      <ClearPatDialog
        dest={clearPat}
        onClose={() => setClearPat(null)}
        onDone={(d) => {
          replace(d);
          setClearPat(null);
          setNotice(`PAT cleared for ${d.database}. Replays are disabled until a new PAT is set.`);
        }}
      />
    </div>
  );
}

function LabelDialog({ dest, onClose, onSaved }: { dest: Destination; onClose: () => void; onSaved: (d: Destination) => void }) {
  const [label, setLabel] = useState(dest.label ?? '');
  const [tenantId, setTenantId] = useState(dest.tenantId ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onSaved(await api.updateDestination(dest.id, { label: label.trim() || null, tenantId: tenantId.trim() || null }));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      title={`Edit ${dest.displayName ?? dest.database}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="label-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form id="label-form" onSubmit={submit}>
        <Field label="Label" hint="Internal name shown in filters. Leave empty to remove.">
          {(id) => <input id={id} value={label} maxLength={200} onChange={(e) => setLabel(e.target.value)} autoFocus />}
        </Field>
        <Field label="Tenant id" hint="Maximizer tenant id (GUID) written into the data-sync metadata. Required for Process.">
          {(id) => <input id={id} value={tenantId} maxLength={200} onChange={(e) => setTenantId(e.target.value)} spellCheck={false} />}
        </Field>
        {error && <Alert>{error}</Alert>}
      </form>
    </Dialog>
  );
}

function PatDialog({ dest, onClose, onSaved }: { dest: Destination; onClose: () => void; onSaved: (d: Destination) => void }) {
  const [pat, setPat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (pat.trim().length < 10) return setError('The PAT must be at least 10 characters.');
    setBusy(true);
    setError(null);
    try {
      onSaved(await api.updateDestination(dest.id, { pat: pat.trim() }));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      title={`${dest.hasPat ? 'Replace' : 'Set'} PAT for ${dest.displayName ?? dest.database}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="pat-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save PAT'}
          </button>
        </>
      }
    >
      <form id="pat-form" onSubmit={submit}>
        <p className="muted">
          Paste a Maximizer personal access token for tenant <code>{dest.database}</code>. It is stored on the
          server and used only for replays. It is write-only: it will never be displayed again.
          {dest.hasPat && ' Saving replaces the current PAT.'}
        </p>
        <Field label="Personal access token">
          {(id) => (
            <input
              id={id}
              type="password"
              value={pat}
              onChange={(e) => setPat(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              required
              minLength={10}
              autoFocus
            />
          )}
        </Field>
        {error && <Alert>{error}</Alert>}
      </form>
    </Dialog>
  );
}

function ClearPatDialog({ dest, onClose, onDone }: { dest: Destination | null; onClose: () => void; onDone: (d: Destination) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setError(null), [dest]);
  return (
    <ConfirmDialog
      open={!!dest}
      title="Clear PAT?"
      confirmLabel="Clear PAT"
      danger
      busy={busy}
      error={error}
      onClose={onClose}
      onConfirm={async () => {
        if (!dest) return;
        setBusy(true);
        try {
          onDone(await api.updateDestination(dest.id, { pat: null }));
        } catch (e) {
          setError(errorMessage(e));
        } finally {
          setBusy(false);
        }
      }}
    >
      <p>
        Replays for <strong>{dest?.displayName ?? dest?.database}</strong> will be disabled until a new PAT is set. New
        batches from advisors are unaffected (they use the advisor's session).
      </p>
    </ConfirmDialog>
  );
}
